import React from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { fetchApiKeys } from '../../../store/slices/apiKeySlice';
import { fetchUsage, fetchActivity } from '../../../store/slices/usageSlice';

function Skeleton({ w = '100%', h = 16, r = 6 }) {
  return <div style={{ width: w, height: h, background: 'var(--line-2)', borderRadius: r, animation: 'shimmer 1.4s ease-in-out infinite' }} />;
}


function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

export default function Overview() {
  const dispatch = useDispatch();
  const { user } = useSelector(s => s.auth);
  const { keys, loading: keysLoading, lastFetched: keysFetched } = useSelector(s => s.apiKeys);
  const { tokens, spend, history, activity, loading: usageLoading, lastFetched: usageFetched } = useSelector(s => s.usage);

  React.useEffect(() => {
    const STALE = 5 * 60 * 1000;
    if (!keysFetched || Date.now() - keysFetched > STALE) dispatch(fetchApiKeys());
    if (!usageFetched || Date.now() - usageFetched > STALE) dispatch(fetchUsage());
    dispatch(fetchActivity());
  }, []); // eslint-disable-line

  function timeAgo(ts) {
    const s = Math.floor((Date.now() - new Date(ts)) / 1000);
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  }

  function engineIcon(engine) {
    if (engine === 'vision') return '👁';
    if (engine === 'embedding') return '⚡';
    return '💬';
  }

  const firstName = user?.first_name || user?.firstName || user?.name?.split(' ')[0] || 'there';
  const bars = history.slice(-30);
  const maxBar = Math.max(...bars, 1);
  const activeKeys = keys?.filter(k => k.status !== 'revoked')?.length ?? 0;

  return (
    <div className="dash-page">
      <div className="dash-page-header">
        <div>
          <p className="dash-sub">OVERVIEW</p>
          <h1 className="dash-page-title">{getGreeting()}, {firstName}.</h1>
          <p className="dash-page-desc">Here's what's happening in your workspace.</p>
        </div>
        <Link to="/dashboard/keys" className="btn btn-primary" style={{ flexShrink: 0, marginTop: 4 }}>
          <svg width="11" height="11" viewBox="0 0 11 11" fill="none"><path d="M5.5 1v9M1 5.5h9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
          New API key
        </Link>
      </div>

      {/* Stat row */}
      <div className="stat-grid">
        <div className="stat-card">
          <span className="stat-label">Tokens · this month</span>
          {usageLoading
            ? <Skeleton h={32} w={100} r={4} />
            : <div className="stat-num">
                {!tokens ? '0' : tokens >= 1e6 ? (tokens / 1e6).toFixed(1) : tokens >= 1000 ? Math.round(tokens / 1000) : tokens}
                <span className="stat-unit">{tokens >= 1e6 ? 'M' : tokens >= 1000 ? 'K' : ''}</span>
              </div>}
          <div className="stat-delta stat-delta-neutral">all keys combined</div>
        </div>
        <div className="stat-card">
          <span className="stat-label">Spend · cycle</span>
          {usageLoading
            ? <Skeleton h={32} w={80} r={4} />
            : <div className="stat-num">${spend ? (spend < 0.01 ? spend.toFixed(4) : spend.toFixed(2)) : '0.00'}</div>}
          <div className="stat-delta stat-delta-neutral">month to date</div>
        </div>
        <div className="stat-card">
          <span className="stat-label">Latency · P50</span>
          <div className="stat-num">284<span className="stat-unit">ms</span></div>
          <div className="stat-delta stat-delta-up">↑ 12ms vs last week</div>
        </div>
        <div className="stat-card">
          <span className="stat-label">Active keys</span>
          {keysLoading
            ? <Skeleton h={32} w={40} r={4} />
            : <div className="stat-num">{activeKeys}</div>}
          <div className="stat-delta stat-delta-neutral">
            <Link to="/dashboard/keys" style={{ color: 'var(--accent)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>Manage →</Link>
          </div>
        </div>
      </div>

      <div className="overview-lower-grid">
        {/* Token usage chart */}
        <div className="dash-card">
          <div className="dash-card-head">
            <div>
              <div className="dash-card-title">Token usage</div>
              <div className="dash-card-desc">Last 30 days · all keys</div>
            </div>
            <Link to="/dashboard/usage" style={{ fontSize: 12, color: 'var(--fg-dim)', fontFamily: 'var(--font-mono)' }}>Full report →</Link>
          </div>
          <div className="dash-card-body">
            {bars.length > 0 ? (
              <>
                <div className="usage-bars" style={{ height: 90 }}>
                  {bars.map((v, i) => (
                    <div key={i}
                      style={{ height: `${Math.max(3, (v / maxBar) * 100)}%`, opacity: 0.35 + (v / maxBar) * 0.65 }}
                      title={`${v.toLocaleString()} tokens`}
                    />
                  ))}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10, fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--fg-faint)', letterSpacing: '0.08em' }}>
                  <span>30 days ago</span><span>Today</span>
                </div>
              </>
            ) : (
              <div style={{ height: 110, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <div style={{ fontSize: 12, color: 'var(--fg-dim)' }}>No usage data yet</div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--fg-faint)', letterSpacing: '0.08em' }}>Data appears after your first API call</div>
              </div>
            )}
          </div>
        </div>

        {/* Recent activity */}
        <div className="dash-card">
          <div className="dash-card-head">
            <div className="dash-card-title">Recent activity</div>
            <Link to="/dashboard/usage" style={{ fontSize: 12, color: 'var(--fg-dim)', fontFamily: 'var(--font-mono)' }}>Full log →</Link>
          </div>
          {activity.length === 0 ? (
            <div className="dash-empty" style={{ padding: '32px 20px' }}>
              <div style={{ width: 36, height: 36, borderRadius: 'var(--r-sm)', background: 'var(--bg-2)', border: '1px solid var(--line)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--fg-dim)' }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 2v4l2.5 2.5M14 8A6 6 0 1 1 2 8a6 6 0 0 1 12 0z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>
              </div>
              <div style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--fg)', marginTop: 4 }}>No recent activity</div>
              <p style={{ fontSize: 12, color: 'var(--fg-dim)', textAlign: 'center', lineHeight: 1.5 }}>Your API calls and chat usage will appear here.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {activity.slice(0, 8).map((item, i) => (
                <div key={item._id || i} style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '10px 20px',
                  borderBottom: i < Math.min(activity.length, 8) - 1 ? '1px solid var(--line)' : 'none',
                }}>
                  <div style={{ width: 28, height: 28, borderRadius: 'var(--r-sm)', background: 'var(--bg-2)', border: '1px solid var(--line)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, flexShrink: 0 }}>
                    {engineIcon(item.engine)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--fg)', textTransform: 'capitalize' }}>
                      {item.engine} · {item.model?.split('/').pop() || item.model}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--fg-dim)', fontFamily: 'var(--font-mono)', marginTop: 1 }}>
                      {item.totalTokens?.toLocaleString() || 0} tokens · {item.latencyMs ? `${item.latencyMs}ms` : '—'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, flexShrink: 0 }}>
                    <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--fg-faint)' }}>{timeAgo(item.createdAt)}</span>
                    <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 8, background: item.status === 'success' ? 'rgba(34,197,94,0.1)' : 'rgba(255,80,80,0.1)', color: item.status === 'success' ? '#22c55e' : '#ff8080' }}>
                      {item.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Quick start — show when no keys */}
      {!keysLoading && activeKeys === 0 && (
        <div className="dash-card" style={{ marginTop: 20 }}>
          <div className="dash-card-body" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '40px 24px', gap: 16 }}>
            <div style={{ width: 52, height: 52, borderRadius: 'var(--r-md)', background: 'rgba(139,124,255,0.1)', border: '1px solid rgba(139,124,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><circle cx="8" cy="9" r="4.5" stroke="var(--accent)" strokeWidth="1.5"/><path d="M11.5 12.5l7 7M16 17l2.5 2.5" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round"/></svg>
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>Generate your first API key</div>
              <p style={{ fontSize: 13, color: 'var(--fg-mid)', maxWidth: '36ch', lineHeight: 1.6 }}>
                One key unlocks every modality — chat, vision, embeddings, and voice.
              </p>
            </div>
            <Link to="/dashboard/keys" className="btn btn-primary">Create API key →</Link>
          </div>
        </div>
      )}
    </div>
  );
}
